import React from 'react';
import * as Icons from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';

// 14 reasons taken from Indovance + tuned for Land Drafters voice
export const WHY_CHOOSE_US_REASONS = [
  { icon: 'BadgeCheck', title: 'ISO Certified', desc: 'ISO 9001:2015 certified operations and quality management.' },
  { icon: 'Crosshair', title: 'Survey-Grade Accuracy', desc: 'Access to expert engineers — done right the first time.' },
  { icon: 'Eye', title: 'Transparency', desc: 'Direct access to a skilled and experienced delivery team.' },
  { icon: 'TrendingUp', title: 'Scalability', desc: 'Ramp resources up or down as your project schedule shifts.' },
  { icon: 'Move', title: 'Flexibility', desc: 'Integrated with your offsite team — we work as your extended unit.' },
  { icon: 'Handshake', title: 'Growth Partnership', desc: 'You stay focused on your core competency — we handle the rest.' },
  { icon: 'ListChecks', title: '3-Stage Quality Check', desc: 'Multi-tier review process ensures every deliverable meets your standards.' },
  { icon: 'Award', title: '20+ Years Experience', desc: 'Two decades of trusted engineering partnership with global companies.' },
  { icon: 'Timer', title: 'Quick Turnaround', desc: 'Fast TAT — typical projects delivered in 24-48 hours, rush options available.' },
  { icon: 'PiggyBank', title: 'Save Up to 54%', desc: 'Cut your CAD/engineering overhead by more than half.' },
  { icon: 'CalendarCheck', title: 'On-Time Delivery', desc: 'Predictable delivery — every time, every milestone.' },
  { icon: 'ShieldCheck', title: 'Secured Server', desc: 'Encrypted servers for data exchange and storage.' },
  { icon: 'Wallet', title: 'Zero Setup Cost', desc: 'No setup fees — start with a trial project and scale on success.' },
  { icon: 'Globe2', title: 'Cultural Alignment', desc: 'Seamless transfer of corporate culture and shared vision.' }
];

const WhyChooseUs = ({ heading = 'Why Choose', brand = 'Land Drafters', subheading = 'As Your CAD Partner?' }) => {
  const { tt } = useLang();
  return (
  <section className="py-20 bg-[#0e1116] text-white relative overflow-hidden" data-testid="why-choose-us">
    <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(148,163,184,0.15),transparent_60%)] pointer-events-none"></div>
    <div className="relative max-w-[1400px] mx-auto px-6">
      <div className="text-center mb-12">
        <div className="text-orange-400 text-[11px] uppercase tracking-[0.25em] font-bold mb-3">{tt('Our edge')}</div>
        <h2 className="text-3xl md:text-5xl font-black leading-tight">
          {tt(heading)} <span className="text-green-500">{brand}</span><br />
          <span className="text-orange-500">{tt(subheading)}</span>
        </h2>
        <p className="text-gray-400 mt-5 max-w-2xl mx-auto">{tt('14 reasons global engineering teams choose us — every one delivered consistently across every project.')}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
        {WHY_CHOOSE_US_REASONS.map((r, i) => {
          const Ic = Icons[r.icon] || Icons.Sparkles;
          const accent = i % 3 === 0 ? 'text-orange-400 border-orange-700/40' : 'text-green-400 border-gray-800/40';
          return (
            <div
              key={r.title}
              data-testid={`why-card-${r.title.replace(/\s+/g, '-').toLowerCase()}`}
              className={`group bg-[#1a1f2e] border ${accent} rounded-lg p-4 flex flex-col items-center text-center hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(148,163,184,0.4)] transition-all duration-300`}
            >
              <div className={`w-12 h-12 rounded-full bg-black/40 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ${i % 3 === 0 ? 'text-orange-400' : 'text-green-400'}`}>
                <Ic className="w-6 h-6" />
              </div>
              <div className="font-black text-white text-sm leading-tight">{tt(r.title)}</div>
              <div className="text-[11px] text-gray-400 mt-2 leading-relaxed">{tt(r.desc)}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

export default WhyChooseUs;
